// =============================================================================
// AddressAutocomplete.jsx
// =============================================================================
// Google Places-backed address input with a suggestion dropdown. Used by
// DeliverySelect (in ChildSections.jsx) plus two admin forms in AdminPages.jsx.
//
// WHY THIS LIVES IN ITS OWN FILE: the dropdown previously lived inside the
// component's own DOM tree (position:absolute, top:100%). That meant any
// ancestor with `overflow:hidden` or `overflow:auto` clipped the dropdown.
// Each parent had different overflow rules so the autocomplete kept breaking
// in new places (RequestDetail edit modal, admin Manage Addresses modal, etc.).
//
// Fix: render the dropdown via React portal at `document.body`. The dropdown
// escapes every ancestor's stacking/overflow context and works regardless of
// where the component is placed. Position is calculated from the input's
// getBoundingClientRect() and recomputed on scroll/resize so it tracks the
// input even when a parent scrolls internally.
//
// Props:
//   value             - current text value
//   onChange(text)    - called as user types / picks a suggestion
//   onSelect(parts)   - called when suggestion picked, with parsed address parts
//                       ({ street, street2, suburb, state, postcode })
//   onVerifiedChange  - optional; flipped true when picked, false when edited
//   placeholder       - input placeholder text
//   inputStyle        - extra style overrides for the input
//   style             - extra style for the container
//
// Requires Google Maps Places API loaded (formConstants.isGoogleMapsReady).
// Localhost dev needs its origin added to the API key's allowed referrers,
// otherwise dropdown silently doesn't appear (RefererNotAllowedMapError).
// =============================================================================
import React from "react";
import ReactDOM from "react-dom";
import { parsePlaceAddress } from "../../utils/addressParse";
import { isGoogleMapsReady } from "../../utils/formConstants";
import { logger } from "../../utils/logger";

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  style,
  inputStyle,
  onVerifiedChange,
  inputId,
  mapsReadyTick,
}) {
  const [ready, setReady] = React.useState(isGoogleMapsReady());
  const [suggestions, setSuggestions] = React.useState([]);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [verified, setVerified] = React.useState(false);
  const [numberMissing, setNumberMissing] = React.useState(false);
  const [dropdownPos, setDropdownPos] = React.useState(null);
  const sessionToken = React.useRef(null);
  const inputRef = React.useRef(null);

  // Wait for Google Maps to load (it's added by a <script> tag at app boot).
  React.useEffect(function () {
    if (ready) return;
    let attempts = 0;
    const interval = setInterval(function () {
      attempts++;
      if (isGoogleMapsReady()) {
        setReady(true);
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        clearInterval(interval);
      } else if (attempts > 120) {
        logger.warn("Google Maps never loaded after 30s — check API key and network");
        clearInterval(interval);
      }
    }, 250);
    return function () {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(
    function () {
      if (ready && !sessionToken.current) {
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    },
    [ready]
  );

  // Non-remounting readiness nudge: when the parent bumps mapsReadyTick (e.g. a
  // moment after the modal opens so Google Maps has time to load), re-check
  // readiness WITHOUT remounting. Callers used to pass mapsReadyTick as a React
  // `key`, which REMOUNTED this component and wiped the user's typed value AND the
  // "verified" flag (so a valid picked address kept showing "not verified").
  // Fixed 2026-08-10 — the internal poll already self-heals; this is belt-and-braces.
  React.useEffect(
    function () {
      if (!ready && isGoogleMapsReady()) {
        setReady(true);
        if (!sessionToken.current) {
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    },
    [mapsReadyTick]
  );

  // Compute the dropdown's screen position from the input's bounding rect.
  // Use document-coordinate (top + scrollY) so it follows the input when the
  // window scrolls. Recompute on any scroll/resize while open.
  function updateDropdownPos() {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 2,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }

  React.useEffect(
    function () {
      if (!showDropdown) return;
      updateDropdownPos();
      // capture=true catches scrolls on ancestor elements too (e.g. modal scroll).
      window.addEventListener("scroll", updateDropdownPos, true);
      window.addEventListener("resize", updateDropdownPos);
      return function () {
        window.removeEventListener("scroll", updateDropdownPos, true);
        window.removeEventListener("resize", updateDropdownPos);
      };
    },
    [showDropdown]
  );

  function handleInput(e) {
    const val = e.target.value;
    if (onChange) onChange(val);
    if (numberMissing) setNumberMissing(false);
    if (verified) {
      setVerified(false);
      if (onVerifiedChange) onVerifiedChange(false);
    }
    const isReady = ready || isGoogleMapsReady();
    if (!ready && isReady) {
      setReady(true);
    }
    if (!isReady || val.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    const svc = new window.google.maps.places.AutocompleteService();
    svc.getPlacePredictions(
      {
        input: val,
        componentRestrictions: { country: "au" },
        types: ["address"],
        sessionToken: sessionToken.current,
      },
      function (predictions, status) {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setSuggestions([]);
          setShowDropdown(false);
          return;
        }
        setSuggestions(predictions);
        setShowDropdown(true);
      }
    );
  }

  function handleSelect(prediction) {
    setShowDropdown(false);
    setSuggestions([]);
    setVerified(true);
    if (onVerifiedChange) onVerifiedChange(true);
    if (onChange) onChange(prediction.description);
    if (!onSelect) return;
    const placeSvc = new window.google.maps.places.PlacesService(document.createElement("div"));
    placeSvc.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["address_components"],
        sessionToken: sessionToken.current,
      },
      function (place, status) {
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        // Pure, unit-tested parser. Falls back to the prediction's main_text when
        // Google omits the street_number, so the number the user clicked isn't lost.
        const parsed = parsePlaceAddress(place.address_components, prediction);
        setNumberMissing(parsed.numberMissing);
        onSelect({
          street: parsed.street,
          street2: parsed.street2,
          suburb: parsed.suburb,
          state: parsed.state,
          postcode: parsed.postcode,
        });
      }
    );
  }

  // The portal-rendered dropdown — positioned in document coordinates, attached
  // to document.body so it escapes any ancestor overflow/clipping.
  const dropdown =
    showDropdown && suggestions.length > 0 && dropdownPos
      ? ReactDOM.createPortal(
          <div
            style={{
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 100000,
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {suggestions.map(function (s) {
              return (
                <button
                  type="button"
                  key={s.place_id}
                  onMouseDown={function (e) {
                    // preventDefault stops the input from blurring before the
                    // click registers — the dropdown unmounts on blur so the
                    // click would otherwise hit nothing.
                    e.preventDefault();
                    handleSelect(s);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: "white",
                    border: "none",
                    borderBottom: "1px solid #f0f0f0",
                    padding: "10px 14px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={function (e) {
                    e.currentTarget.style.background = "#f5faf9";
                  }}
                  onMouseLeave={function (e) {
                    e.currentTarget.style.background = "white";
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{s.structured_formatting.main_text}</span>
                  <span style={{ color: "#888", marginLeft: 6 }}>
                    {s.structured_formatting.secondary_text}
                  </span>
                </button>
              );
            })}
            <div style={{ padding: "6px 14px", fontSize: 11, color: "#aaa", textAlign: "right" }}>
              Powered by Google
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div style={Object.assign({ position: "relative" }, style)}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={value}
        onChange={handleInput}
        onBlur={function () {
          setTimeout(function () {
            setShowDropdown(false);
          }, 150);
        }}
        onFocus={function () {
          if (!ready && isGoogleMapsReady()) {
            setReady(true);
            sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          }
          if (suggestions.length > 0) setShowDropdown(true);
        }}
        placeholder={
          ready ? placeholder || "Start typing an address..." : placeholder || "Address..."
        }
        autoComplete="off"
        style={inputStyle}
      />
      {value && !verified && !showDropdown && (
        <div
          style={{
            fontSize: 11,
            color: "#E65100",
            marginTop: 3,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ⚠️ Address not verified — select from the dropdown to confirm, or continue with manual
          entry.
        </div>
      )}
      {numberMissing && (
        <div
          style={{
            fontSize: 11,
            color: "#E65100",
            marginTop: 3,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ⚠️ Please confirm the street number for this address — we couldn't verify it automatically.
          You can edit the address before saving.
        </div>
      )}
      {dropdown}
    </div>
  );
}
